import apiService from '@/services/apiService'

// Initial state
const state = {
  visualIdentitySystems: [],
  currentVIS: null,
  visMaterials: [],
  loading: false,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    pageSize: 10
  },
  filters: {
    brand: null,
    searchTerm: ''
  }
}

// Getters
const getters = {
  allVIS: state => state.visualIdentitySystems,
  currentVIS: state => state.currentVIS,
  visMaterials: state => state.visMaterials,
  isLoading: state => state.loading,
  hasError: state => !!state.error,
  errorMessage: state => state.error,
  pagination: state => state.pagination,
  filters: state => state.filters,
  
  // Get VIS filtered by latest (most recently created)
  latestVIS: state => {
    return [...state.visualIdentitySystems].sort((a, b) => {
      return new Date(b.created_at) - new Date(a.created_at);
    });
  },
  
  // Get VIS filtered by brand
  filteredVIS: state => {
    let filtered = [...state.visualIdentitySystems];
    
    // Apply brand filter if set
    if (state.filters.brand) {
      filtered = filtered.filter(vis => vis.brand === state.filters.brand);
    }
    
    // Apply search term filter if set
    if (state.filters.searchTerm) {
      const term = state.filters.searchTerm.toLowerCase();
      filtered = filtered.filter(vis => 
        vis.brand_name.toLowerCase().includes(term) || 
        vis.version.toLowerCase().includes(term)
      );
    }
    
    return filtered;
  }
}

// Actions
const actions = {
  // Fetch all Visual Identity Systems
  async fetchVISList({ commit }, { page = 1, pageSize = 10 } = {}) {
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      
      const response = await apiService.get('/brands/vis/', {
        params: {
          page,
          page_size: pageSize
        }
      });
      
      // Support both paginated (object with `results` & `count`) and plain array responses
      const visList = Array.isArray(response.data)
        ? response.data
        : (response.data.results || []);

      // Total items/pages depend on response structure
      const totalItems = Array.isArray(response.data)
        ? visList.length
        : response.data.count;

      commit('SET_VIS_LIST', visList);
      commit('SET_PAGINATION', {
        currentPage: page,
        totalPages: Math.ceil((totalItems || 0) / pageSize) || 1,
        totalItems: totalItems || 0,
        pageSize
      });
      
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to fetch Visual Identity Systems');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  // Fetch a single Visual Identity System by ID
  async fetchVISById({ commit }, id) {
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      
      const response = await apiService.get(`/brands/vis/${id}/`);
      commit('SET_CURRENT_VIS', response.data);
      
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.message || `Failed to fetch VIS with ID: ${id}`);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  // Create a new Visual Identity System
  async createVIS({ commit }, visData) {
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      
      // Use FormData if there are file uploads
      let formData = new FormData();
      
      // Append all form fields to FormData
      for (const [key, value] of Object.entries(visData)) {
        if (key === 'geographical_footprint' && Array.isArray(value)) {
          // Handle array fields differently
          value.forEach(id => {
            formData.append('geographical_footprint', id);
          });
        } else if (value !== null && value !== undefined) {
          formData.append(key, value);
        }
      }
      
      const response = await apiService.post('/brands/vis/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Add the new VIS to the list
      commit('ADD_VIS', response.data);
      
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to create Visual Identity System');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  // Update an existing Visual Identity System
  async updateVIS({ commit }, { id, visData }) {
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      
      // Use FormData for file uploads
      let formData = new FormData();
      
      // Append all form fields to FormData
      for (const [key, value] of Object.entries(visData)) {
        if (key === 'geographical_footprint' && Array.isArray(value)) {
          // Handle array fields differently
          value.forEach(id => {
            formData.append('geographical_footprint', id);
          });
        } else if (value !== null && value !== undefined) {
          formData.append(key, value);
        }
      }
      
      const response = await apiService.put(`/brands/vis/${id}/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Update the VIS in the list
      commit('UPDATE_VIS', { id, vis: response.data });
      
      // If this is the current VIS, update it too
      if (state.currentVIS && state.currentVIS.id === id) {
        commit('SET_CURRENT_VIS', response.data);
      }
      
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.message || `Failed to update VIS with ID: ${id}`);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  // Delete a Visual Identity System
  async deleteVIS({ commit }, id) {
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      
      await apiService.delete(`/brands/vis/${id}/`);
      
      // Remove the VIS from the list
      commit('REMOVE_VIS', id);
      
      // If this is the current VIS, clear it
      if (state.currentVIS && state.currentVIS.id === id) {
        commit('SET_CURRENT_VIS', null);
      }
      
      return true;
    } catch (error) {
      commit('SET_ERROR', error.message || `Failed to delete VIS with ID: ${id}`);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  // Fetch Materials for a specific VIS
  async fetchVISMaterials({ commit }, visId) {
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      
      const response = await apiService.get('/brands/vis-materials/', {
        params: { vis_id: visId }
      });
      
      // Support both paginated and plain array responses
      const materials = Array.isArray(response.data)
        ? response.data
        : (response.data.results || []);

      commit('SET_VIS_MATERIALS', materials);
      
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to fetch VIS materials');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  // Set filters
  setFilters({ commit }, filters) {
    commit('SET_FILTERS', filters);
  },
  
  // Fetch all Visual Identity Systems without pagination (for dropdowns)
  async fetchVisualIdentitySystems({ commit }) {
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      
      const response = await apiService.get('/brands/vis/', {
        params: {
          page_size: 100 // Fetch more items to ensure we get all VIS options
        }
      });
      
      // Support both paginated and plain array responses
      return Array.isArray(response.data)
        ? response.data
        : (response.data.results || []);
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to fetch Visual Identity Systems');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },
}

// Mutations
const mutations = {
  SET_LOADING(state, isLoading) {
    state.loading = isLoading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  SET_VIS_LIST(state, visList) {
    state.visualIdentitySystems = visList;
  },
  SET_CURRENT_VIS(state, vis) {
    state.currentVIS = vis;
  },
  SET_VIS_MATERIALS(state, materials) {
    state.visMaterials = materials;
  },
  SET_PAGINATION(state, pagination) {
    state.pagination = {
      ...state.pagination,
      ...pagination
    };
  },
  SET_FILTERS(state, filters) {
    state.filters = {
      ...state.filters,
      ...filters
    };
  },
  ADD_VIS(state, vis) {
    state.visualIdentitySystems.push(vis);
  },
  UPDATE_VIS(state, { id, vis }) {
    const index = state.visualIdentitySystems.findIndex(item => item.id === id);
    if (index !== -1) {
      state.visualIdentitySystems.splice(index, 1, vis);
    }
  },
  REMOVE_VIS(state, id) {
    state.visualIdentitySystems = state.visualIdentitySystems.filter(vis => vis.id !== id);
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
} 